import { RTMFilter, filterElement, Filter, FilterStatus } from '../types/RTMTypes';
import { t } from 'i18next';
import _ from 'lodash';

/**
 * Prepares the initial structure of the filter, organizing products into families and product types.
 * This function is used when the application first loads.
 */
export const prepareStructureOfFilter = (products: any) => {
  let tableFilter = {
    families: new Map(),
    productTypes: new Map(),
    products: new Map(),
  };

  products.forEach((product: any) => {
    const familyId: string = product.productType.letter;
    const productTypeId: string = product.productType.id;
    const productId: string = product.id;

    if (!tableFilter.families.has(familyId)) {
      tableFilter.families.set(familyId, {
        id: familyId,
        name: t(`letters.${familyId}`),
        status: 'all',
        productTypes: new Set([productTypeId]),
        products: new Set([productId]),
      });
    } else {
      let family = tableFilter.families.get(familyId);
      family.productTypes.add(productTypeId);
      family.products.add(productId);
    }

    if (!tableFilter.productTypes.has(productTypeId)) {
      tableFilter.productTypes.set(productTypeId, {
        id: productTypeId,
        name: product.productType.name,
        status: 'all',
        family: familyId,
        products: new Set([productId]),
      });
    } else {
      let productType = tableFilter.productTypes.get(productTypeId);
      productType.products.add(productId);
    }

    if (!tableFilter.products.has(productId)) {
      tableFilter.products.set(productId, {
        id: productId,
        name: product.name ? product.name : `${product.productType.name} ${product.puuid.slice(-4)}`,
        status: 'all',
        family: familyId,
        productTypes: new Set([productTypeId]),
      });
    } else {
      let productItem = tableFilter.products.get(productId);
      productItem.productTypes.add(productTypeId);
      productItem.family = familyId;
    }
  });

  return {
    families: Array.from(tableFilter.families.values()).map((family) => ({
      ...family,
      productTypes: Array.from(family.productTypes),
      products: Array.from(family.products),
    })),
    productTypes: Array.from(tableFilter.productTypes.values()).map((productType) => ({
      ...productType,
      products: Array.from(productType.products),
    })),
    products: Array.from(tableFilter.products.values()).map((product) => ({
      ...product,
      productTypes: Array.from(product.productTypes),
    })),
  };
};

/**
 * Sets the filter to include all products, families, and product types.
 * This function is useful when resetting filters or ensuring all data is loaded initially.
 */
export const setFilterToAll = (products: any): Filter => {
  const filterPrepared: Filter = {
    area: [],
    environment: [],
    familyId: [],
    gender: [],
    productId: [],
    productTypeId: [],
    type: [],
  };

  products.forEach(({ id, productTypeId, productType }: any) => {
    if (id) filterPrepared.productId.includes(id) || filterPrepared.productId.push(id);
    if (productTypeId) filterPrepared.productTypeId.includes(productTypeId) || filterPrepared.productTypeId.push(productTypeId);
    if (productType?.letter && !filterPrepared.familyId.includes(productType.letter)) {
      filterPrepared.familyId.push(productType.letter);
    }
  });

  return filterPrepared;
};

/**
 * Prepares the filter body for product requests based on the current state.
 * This function extracts only the selected items from the filter.
 */
export const prepareBodyToGetProductsByFilter = (tableFilter: RTMFilter): Filter => {
  const { families, productTypes, products } = tableFilter;
  const filterPrepared: Filter = {
    area: [],
    environment: [],
    familyId: [],
    gender: [],
    productId: [],
    productTypeId: [],
    type: [],
  };
  filterPrepared.familyId = families.filter((family) => family.status).map((family) => family.id);
  filterPrepared.productTypeId = productTypes
    .filter((productType) => productType.status)
    .map((productType) => Number(productType.id));
  filterPrepared.productId = products.filter((product) => product.status).map((product) => Number(product.id));
  return filterPrepared;
};

/**
 * Handles the state change of an element in the filter.
 * Updates the filter state when an item is checked or unchecked.
 */
export const handleFilter = (
  prevState: RTMFilter,
  type: 'families' | 'productTypes' | 'products',
  id: filterElement['id'],
  newStatus: FilterStatus
): RTMFilter => {
  const updatedFilter: RTMFilter = _.cloneDeep({ ...prevState });

  switch (type) {
    case 'families':
      updateFamilyStatus(updatedFilter, id, newStatus);
      break;
    case 'productTypes':
      updateProductTypeStatus(updatedFilter, id, newStatus);
      break;
    case 'products':
      updateProductStatus(updatedFilter, id, newStatus);
      break;
  }

  return updatedFilter;
};

/**
 * Updates the status of a family and its related elements.
 */
const updateFamilyStatus = (updatedFilter: RTMFilter, familyId: string, currentStatus: FilterStatus) => {
  const family = updatedFilter.families.find((f) => f.id === familyId);
  if (!family) return;

  const newFamilyStatus = currentStatus !== 'all' ? 'all' : 'none';
  family.status = newFamilyStatus;

  updatedFilter.products.forEach((product) => {
    if (family.products?.includes(product.id)) {
      product.status = newFamilyStatus;
    }
  });

  updatedFilter.productTypes.forEach((productType) => {
    if (family.productTypes?.includes(productType.id)) {
      productType.status = newFamilyStatus;
    }
  });
};

/**
 * Updates the status of a product type and its related products.
 */
const updateProductTypeStatus = (updatedFilter: RTMFilter, productTypeId: string, currentStatus: FilterStatus) => {
  const productType = updatedFilter.productTypes.find((pt) => pt.id === productTypeId);
  if (!productType) return;

  const newProductTypeStatus = currentStatus !== 'all' ? 'all' : 'none';
  productType.status = newProductTypeStatus;

  const family = updatedFilter.families.find((f) => f.id === productType.family);
  if (family && newProductTypeStatus === 'none') {
    const allProductTypesChecked = family.productTypes?.every(
      (ptId) => updatedFilter.productTypes.find((pt) => pt.id === ptId)?.status === 'none'
    );
    family.status = allProductTypesChecked ? 'none' : 'some';
  } else if (family && newProductTypeStatus === 'all') {
    const allProductTypesChecked = family.productTypes?.every(
      (ptId) => updatedFilter.productTypes.find((pt) => pt.id === ptId)?.status === 'all'
    );
    family.status = allProductTypesChecked ? 'all' : 'some';
  }

  updatedFilter.products.forEach((product) => {
    if (productType.products?.includes(product.id)) {
      product.status = newProductTypeStatus;
    }
  });
};

/**
 * Updates the status of a product and its relationships with productTypes and families.
 */
const updateProductStatus = (updatedFilter: RTMFilter, productId: string, currentStatus: FilterStatus) => {
  const product = updatedFilter.products.find((p) => p.id === productId);
  if (!product) return;

  const newProductStatus = currentStatus !== 'all' ? 'all' : 'none';
  product.status = newProductStatus;

  product.productTypes?.forEach((productTypeId) => {
    const productType = updatedFilter.productTypes.find((pt) => pt.id === productTypeId);
    if (!productType) return;

    const allProductsChecked = productType.products?.every(
      (pId) => updatedFilter.products.find((p) => p.id === pId)?.status === 'all'
    );
    const someProductsChecked = productType.products?.some(
      (pId) => updatedFilter.products.find((p) => p.id === pId)?.status === 'all'
    );

    productType.status = allProductsChecked ? 'all' : someProductsChecked ? 'some' : 'none';
  });

  const family = updatedFilter.families.find((f) => f.id === product.family);
  if (family) {
    const allFamilyProductsChecked = family.products?.every(
      (pId) => updatedFilter.products.find((p) => p.id === pId)?.status === 'all'
    );
    const someFamilyProductsChecked = family.products?.some(
      (pId) => updatedFilter.products.find((p) => p.id === pId)?.status === 'all'
    );

    const allFamilyTypesChecked = family.productTypes?.every(
      (ptId) => updatedFilter.productTypes.find((pt) => pt.id === ptId)?.status === 'all'
    );
    const someFamilyTypesChecked = family.productTypes?.some(
      (ptId) => updatedFilter.productTypes.find((pt) => pt.id === ptId)?.status === 'all'
    );

    family.status =
      allFamilyProductsChecked && allFamilyTypesChecked
        ? 'all'
        : someFamilyProductsChecked || someFamilyTypesChecked
        ? 'some'
        : 'none';
  }
};
